import { appendReimbursementIdForDefaultPage } from '@/features/resident/utils/residentDecoupling'
import type { ReimbursementExtendedFields, ReimbursementMilestoneId } from '../types/reimbursementFormTypes'
import { appendReimbursement3ScalarFields } from './buildReimbursementExtendedPayload'

const PAYLOAD_METADATA_KEYS = new Set([
  'page',
  'application_id',
  'send_to',
  'reimbursement_section_submit',
  'reimbursement_section_unlock',
  'reimbursement_submit_for_review',
  'claim_override',
])

const CORE_FORM_KEYS = new Set([
  'first_month_not_paid_month',
  'first_month_not_paid_year',
  'first_month_not_paid',
  'unused_security_deposit',
])

const CORE_EXTENDED_API_KEYS = new Set(['unit_transfer_after_movein'])

const UNIQUE_FORM_KEYS = new Set(['reason_for_default', 'covid_19'])

const UNIQUE_EXTENDED_API_KEYS = new Set([
  'notice_required',
  'vacate_before_eviction_lawsuit_filed',
  'alternate_court_issued_document',
  'using_third_party_eviction_services',
  'third_party_servicer_name',
  'utilizing_legal_team_for_eviction',
  'bankruptcy_applicable',
  'appeal_applicable',
  'resident_paid_into_court',
  'stipulation_applicable',
  'resident_defaulted_on_repayment',
])

const FINAL_FORM_KEYS = new Set([
  'moveout_date',
  'new_resident_lease_start_date',
  'movein_date',
  'is_final_ledger',
])

function allowedKeysForMilestone(milestoneId: ReimbursementMilestoneId): Set<string> {
  const allowed = new Set<string>(PAYLOAD_METADATA_KEYS)
  if (milestoneId === 1) {
    for (const key of CORE_FORM_KEYS) allowed.add(key)
    for (const key of CORE_EXTENDED_API_KEYS) allowed.add(key)
  } else if (milestoneId === 2) {
    for (const key of UNIQUE_FORM_KEYS) allowed.add(key)
    for (const key of UNIQUE_EXTENDED_API_KEYS) allowed.add(key)
  } else if (milestoneId === 3) {
    for (const key of FINAL_FORM_KEYS) allowed.add(key)
  }
  return allowed
}

function isLossAdjustedReasonKey(key: string): boolean {
  return key === 'loss_adjusted_reason[]' || /^loss_adjusted_reason\[\d+\]$/.test(key)
}

/**
 * Strip reimbursement save payload to fields belonging to the clicked milestone section.
 * When `reimbursementId` is provided, attaches `reimbursement_id` after scoping (default page only).
 */
export function scopeReimbursementSavePayload(
  payload: Record<string, unknown>,
  milestoneId: ReimbursementMilestoneId,
  reimbursementId?: string | number,
): void {
  const allowed = allowedKeysForMilestone(milestoneId)
  for (const key of Object.keys(payload)) {
    if (allowed.has(key)) continue
    if (milestoneId === 3 && isLossAdjustedReasonKey(key)) continue
    delete payload[key]
  }
  appendReimbursementIdForDefaultPage(payload, reimbursementId)
}

/** Append extended scalars for one section only (core / unique / final). */
export function appendReimbursement3ScalarFieldsForSection(
  payload: Record<string, unknown>,
  extended: ReimbursementExtendedFields,
  milestoneId: ReimbursementMilestoneId,
): void {
  const full: Record<string, unknown> = {}
  appendReimbursement3ScalarFields(full, extended)
  const allowed =
    milestoneId === 1
      ? CORE_EXTENDED_API_KEYS
      : milestoneId === 2
        ? UNIQUE_EXTENDED_API_KEYS
        : new Set<string>()
  for (const [key, value] of Object.entries(full)) {
    if (allowed.has(key) && value !== undefined) {
      payload[key] = value
    }
  }
}
